import { ErrorOutline as ErrorOutlineIcon } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from "@mui/material";
import type React from "react";
import { MessageWithLinks } from "~/components/ui/messageWithLinks";
import { DIALOG_MESSAGES } from "~/constants/messages/dialogMessages";

export interface LookupTypeBlockDialogProps {
  open: boolean;
  errors: string[];
  onClose: () => void;
  onNavigateToFieldDesign?: () => void;
}

const TITLE_ID = "lookup-type-block-dialog-title";
const DESC_ID = "lookup-type-block-dialog-description";

export const LookupTypeBlockDialog: React.FC<LookupTypeBlockDialogProps> = ({
  open,
  errors,
  onClose,
  onNavigateToFieldDesign,
}) => {
  const messages = DIALOG_MESSAGES.LOOKUP_TYPE_BLOCK;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby={TITLE_ID}
      aria-describedby={DESC_ID}
      PaperProps={{ sx: { borderRadius: 3, maxHeight: "85vh" } }}
    >
      <DialogTitle id={TITLE_ID} sx={{ pb: 1 }}>
        <Typography
          component="span"
          variant="h6"
          fontWeight={700}
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <ErrorOutlineIcon color="error" aria-hidden="true" />
          {messages.TITLE}
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ px: 3, py: 2 }}>
        <Typography id={DESC_ID} variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {messages.DESCRIPTION}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          <MessageWithLinks text={messages.HELP_LINK_TEXT} />
        </Typography>

        <Typography
          variant="caption"
          color="text.secondary"
          component="p"
          sx={{ mb: 0.5, fontWeight: 600 }}
        >
          {messages.ERROR_LIST_LABEL}（{errors.length}件）
        </Typography>

        <ul aria-label={messages.ERROR_LIST_LABEL} style={{ margin: 0, paddingLeft: "1.5em" }}>
          {errors.map((error) => (
            <li key={error}>
              <Typography variant="body2" color="error" sx={{ whiteSpace: "pre-line" }}>
                {error}
              </Typography>
            </li>
          ))}
        </ul>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
        <Button variant="text" color="inherit" onClick={onClose}>
          {messages.CLOSE_BUTTON}
        </Button>
        {onNavigateToFieldDesign !== undefined && (
          <Button
            variant="contained"
            color="primary"
            onClick={onNavigateToFieldDesign}
            sx={{ borderRadius: 2, px: 3 }}
          >
            {messages.NAVIGATE_TO_FIELD_DESIGN_BUTTON}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
